import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import './MediaModal.css';

const MediaModal = ({ isOpen, onClose, conversation, onConfirmSelection, resetToken }) => {
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [sentMedia, setSentMedia] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('photos'); // 'photos' or 'videos'
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState('all');
  const scrollContainerRef = useRef(null);
  const sentinelRef = useRef(null);
  const loadedPhotoPagesRef = useRef(new Set());
  const loadedVideoPagesRef = useRef(new Set());
  const inFlightRef = useRef(false);
  
  // Pagination state
  const [photosPage, setPhotosPage] = useState(1);
  const [videosPage, setVideosPage] = useState(1);
  const [hasMorePhotos, setHasMorePhotos] = useState(true);
  const [hasMoreVideos, setHasMoreVideos] = useState(true);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const PAGE_SIZE = 20; // Number of items per page

  const fetchCollections = async () => {
    if (!isOpen) return;
    const jwt = Cookies.get('user_token');
    const headers = { Authorization: `Bearer ${jwt}` };
    const subscriptionId = conversation.subscriptionId;

    try {
      const response = await axios.post('https://unbot.io/api/v1.0/proxy', {
        method: 'GET',
        path: 'v2.0/collections/',
        parameters: { type: 'cloud' },
        subscriptionId,
      }, { headers });
      setCollections(response.data || []);
    } catch (error) {
      console.error("Erreur lors de la récupération des collections:", error);
    }
  };

  const fetchMedia = async (reset = false) => {
    if (!isOpen) return;
    setIsLoading(reset); // Only show full loader on reset
    if (reset) {
        setPhotos([]);
        setVideos([]);
        setPhotosPage(1);
        setVideosPage(1);
        setHasMorePhotos(true);
        setHasMoreVideos(true);
        loadedPhotoPagesRef.current = new Set();
        loadedVideoPagesRef.current = new Set();
    }

    const jwt = Cookies.get('user_token');
    const headers = { Authorization: `Bearer ${jwt}` };
    const subscriptionId = conversation.subscriptionId;

    const parameters = {
      type: 'all',
      start: 0,
      end: PAGE_SIZE,
      uploadedOnly: true,
      collectionId: selectedCollection === 'all' ? undefined : selectedCollection,
    };

    try {
      // Fetch photos and videos in parallel
      const [photosRes, videosRes] = await Promise.all([
        axios.post('https://unbot.io/api/v1.0/proxy', {
          method: 'GET',
          path: 'v4.0/photos/uploaded',
          parameters,
          subscriptionId,
        }, { headers }),
        axios.post('https://unbot.io/api/v1.0/proxy', {
          method: 'GET',
          path: 'v3.0/videos/uploaded',
          parameters,
          subscriptionId,
        }, { headers }),
      ]);

      const uniquePhotos = Array.from(new Map((photosRes.data || []).map(m => [m?._id, m])).values());
      const uniqueVideos = Array.from(new Map((videosRes.data || []).map(m => [m?._id, m])).values());
      setPhotos(uniquePhotos);
      setVideos(uniqueVideos);
      setHasMorePhotos(photosRes.data.length === PAGE_SIZE);
      setHasMoreVideos(videosRes.data.length === PAGE_SIZE);
      setPhotosPage(2); // Next page to fetch will be 2
      setVideosPage(2);
      loadedPhotoPagesRef.current.add(1);
      loadedVideoPagesRef.current.add(1);

    } catch (error) {
      console.error("Error fetching media:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreMedia = useCallback(async () => {
    if (inFlightRef.current || isFetchingNextPage) return;

    const jwt = Cookies.get('user_token');
    const headers = { Authorization: `Bearer ${jwt}` };
    const subscriptionId = conversation.subscriptionId;
    const isPhotoTab = activeTab === 'photos';
    const pageToFetch = isPhotoTab ? photosPage : videosPage;
    const loadedSet = isPhotoTab ? loadedPhotoPagesRef.current : loadedVideoPagesRef.current;
    if (loadedSet.has(pageToFetch)) return;

    inFlightRef.current = true;
    setIsFetchingNextPage(true);

    const parameters = {
        type: 'all',
        start: (pageToFetch - 1) * PAGE_SIZE,
        end: pageToFetch * PAGE_SIZE, // Correctly calculate the end index
        uploadedOnly: true,
        collectionId: selectedCollection === 'all' ? undefined : selectedCollection,
    };

    try {
        const response = await axios.post('https://unbot.io/api/v1.0/proxy', {
            method: 'GET',
            path: isPhotoTab ? 'v4.0/photos/uploaded' : 'v3.0/videos/uploaded',
            parameters,
            subscriptionId,
        }, { headers });

        if (isPhotoTab) {
            setPhotos(prev => {
              const map = new Map((prev || []).map(m => [m?._id, m]));
              for (const item of response.data || []) {
                if (item && item._id && !map.has(item._id)) map.set(item._id, item);
              }
              return Array.from(map.values());
            });
            setHasMorePhotos(response.data.length === PAGE_SIZE);
            setPhotosPage(prev => prev + 1);
            loadedSet.add(pageToFetch);
        } else {
            setVideos(prev => {
              const map = new Map((prev || []).map(m => [m?._id, m]));
              for (const item of response.data || []) {
                if (item && item._id && !map.has(item._id)) map.set(item._id, item);
              }
              return Array.from(map.values());
            });
            setHasMoreVideos(response.data.length === PAGE_SIZE);
            setVideosPage(prev => prev + 1);
            loadedSet.add(pageToFetch);
        }

    } catch (error) {
        console.error("Error fetching more media:", error);
    } finally {
        setIsFetchingNextPage(false);
        inFlightRef.current = false;
    }
  }, [isFetchingNextPage, activeTab, photosPage, videosPage, PAGE_SIZE, selectedCollection, conversation?.subscriptionId]);

  const fetchSentMedia = async () => {
    if (!isOpen) return;
    const jwt = Cookies.get('user_token');
    const headers = { Authorization: `Bearer ${jwt}` };
    const subscriptionId = conversation.subscriptionId;
    const fanId = conversation.fanId;
    if (!fanId) return;

    try {
        const [sentPhotosRes, sentVideosRes] = await Promise.all([
            axios.post('https://unbot.io/api/v1.0/proxy', {
                method: 'GET',
                path: 'v3.0/messages/mediaSent',
                parameters: { type: 'image', fanId, start: 0, end: 100000 },
                subscriptionId,
            }, { headers }),
            axios.post('https://unbot.io/api/v1.0/proxy', {
                method: 'GET',
                path: 'v3.0/messages/mediaSent',
                parameters: { type: 'video', fanId, start: 0, end: 100000 },
                subscriptionId,
            }, { headers }),
        ]);

        const allSentMedia = [...sentPhotosRes.data, ...sentVideosRes.data];
        setSentMedia(allSentMedia);

    } catch (error) {
        console.error("Error fetching sent media:", error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchCollections();
      fetchSentMedia();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      fetchMedia(true);
    }
  }, [isOpen, selectedCollection]);

  useEffect(() => {
    if (typeof resetToken !== 'undefined') {
      setSelectedMedia([]);
    }
  }, [resetToken]);

  useEffect(() => {
    if (!isOpen) return;
    const root = scrollContainerRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const hasMore = activeTab === 'photos' ? hasMorePhotos : hasMoreVideos;
        if (entry.isIntersecting && hasMore && !isFetchingNextPage) {
          loadMoreMedia();
        }
      },
      { root, rootMargin: '200px 0px 200px 0px', threshold: 0.01 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isOpen, activeTab, hasMorePhotos, hasMoreVideos, isFetchingNextPage, loadMoreMedia]);

  useEffect(() => {
    if (!isOpen) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const hasMore = activeTab === 'photos' ? hasMorePhotos : hasMoreVideos;
    if (!hasMore || isFetchingNextPage) return;
    const notOverflowing = container.scrollHeight <= container.clientHeight + 50;
    if (notOverflowing) {
      loadMoreMedia();
    }
  }, [isOpen, photos.length, videos.length, activeTab, hasMorePhotos, hasMoreVideos, isFetchingNextPage, loadMoreMedia]);

  const handleConfirm = () => {
    onConfirmSelection(selectedMedia);
    onClose();
  };

  const handleMediaSelect = (media) => {
    setSelectedMedia(prev => {
      if (prev.find(m => m._id === media._id)) {
        return prev.filter(m => m._id !== media._id);
      } else {
        return [...prev, media];
      }
    });
  };

  const renderMediaGrid = (mediaList) => {
    return (
      <div className="media-select-modal-grid">
        {mediaList
          .filter(media => media.src?.medium) // On filtre les médias qui n'ont pas de src.medium
          .map(media => {
            const sentInfo = sentMedia.find(sm => sm._id === media._id || sm.mediaOriginId === media._id);
            const isSelected = selectedMedia.find(m => m._id === media._id);

            return (
              <div
                key={media._id}
                className={`media-select-modal-item ${isSelected ? 'selected' : ''}`}
                onClick={() => handleMediaSelect(media)}
              >
                <img src={media.src.medium} alt={media.mediaType} className="media-select-modal-thumbnail" />
                {sentInfo && (
                  <div className="media-select-modal-sent-overlay">
                    Envoyé {sentInfo.amount > 0 ? `à ${sentInfo.amount}€` : ''}
                    {sentInfo.bought && <span> (Acheté)</span>}
                  </div>
                )}
                <div className="media-select-modal-select-indicator">✓</div>
              </div>
            );
          })}
      </div>
    );
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="media-select-modal-overlay">
      <div className="media-select-modal-content">
        <button onClick={onClose} className="media-select-modal-close-button">×</button>
        <div className="media-select-modal-header">
          <div className="media-select-modal-header-left">
            <h2>Uncove Cloud</h2>
            <div className="media-select-modal-collection-filter">
              <select 
                value={selectedCollection} 
                onChange={(e) => setSelectedCollection(e.target.value)}
                className="media-select-modal-collection-select"
              >
                <option value="all">Toutes les collections</option>
                {collections.map(collection => (
                  <option key={collection._id} value={collection._id}>
                    {collection.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="media-select-modal-tabs">
            <button 
              className={`media-select-modal-tab ${activeTab === 'photos' ? 'active' : ''}`}
              onClick={() => setActiveTab('photos')}
            >PHOTO{photos.length > 1 ? 'S' : ''}
            </button>
            <button 
              className={`media-select-modal-tab ${activeTab === 'videos' ? 'active' : ''}`}
              onClick={() => setActiveTab('videos')}
            >VIDÉO{videos.length > 1 ? 'S' : ''}
            </button>
          </div>
        </div>
        <div className="media-select-modal-body" ref={scrollContainerRef}>
          {isLoading ? (
            <p>Chargement des médias...</p>
          ) : (
            <>
              {activeTab === 'photos' ? renderMediaGrid(photos) : renderMediaGrid(videos)}
              <div className="media-select-modal-load-more-container">
                {isFetchingNextPage && <span>Chargement...</span>}
                <div ref={sentinelRef} />
              </div>
            </>
          )}
        </div>
        <div className="media-select-modal-footer">
          {selectedMedia.length > 0 ? (
            <button className="media-select-modal-confirm-media-button" onClick={handleConfirm}>
              Ajouter {selectedMedia.length} média{selectedMedia.length > 1 ? 's' : ''}
            </button>
          ) : (
            <button className="media-select-modal-close-footer-button" onClick={onClose}>
              Fermer
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaModal;
